import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../context/LanguageContext';

const { FiAlertCircle, FiShield, FiMessageSquare, FiX } = FiIcons;
const { FaWhatsapp, FaViber, FaTelegram } = FaIcons;

const SOSButton = ({ onTrigger, isDisabled }) => {
  const [step, setStep] = useState('idle');
  const { t } = useTranslation();

  const handleInitialClick = () => {
    setStep('confirm');
    setTimeout(() => {
      if (step === 'confirm') setStep('idle');
    }, 4000);
  };

  const handleConfirmClick = () => {
    setStep('select');
  };

  const handleAction = (type) => {
    onTrigger(type);
    setStep('idle');
  };

  return (
    <div className="relative">
      <AnimatePresence>
        {step === 'confirm' && (
          <motion.div 
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className="absolute -top-14 left-1/2 -translate-x-1/2 whitespace-nowrap bg-red-600 text-white text-[10px] font-black px-4 py-1.5 rounded-full shadow-2xl z-20 uppercase tracking-tighter"
          >
            {t('confirm_sos')}
          </motion.div>
        )}
        {step === 'select' && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute -top-32 left-1/2 -translate-x-1/2 bg-white p-2.5 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-slate-100 grid grid-cols-2 gap-2.5 z-30 w-52"
          >
            <button onClick={() => handleAction('whatsapp')} className="p-4 bg-[#25D366] text-white rounded-2xl hover:bg-[#128C7E] transition-all shadow-lg flex items-center justify-center group/btn">
              <SafeIcon icon={FaWhatsapp} className="text-2xl group-active/btn:scale-90 transition-transform" />
            </button>
            <button onClick={() => handleAction('telegram')} className="p-4 bg-[#0088cc] text-white rounded-2xl hover:bg-[#0077b5] transition-all shadow-lg flex items-center justify-center group/btn">
              <SafeIcon icon={FaTelegram} className="text-2xl group-active/btn:scale-90 transition-transform" />
            </button>
            <button onClick={() => handleAction('viber')} className="p-4 bg-[#7360f2] text-white rounded-2xl hover:bg-[#5d4dbd] transition-all shadow-lg flex items-center justify-center group/btn">
              <SafeIcon icon={FaViber} className="text-2xl group-active/btn:scale-90 transition-transform" />
            </button>
            <button onClick={() => handleAction('sms')} className="p-4 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center group/btn">
              <SafeIcon icon={FiMessageSquare} className="text-2xl group-active/btn:scale-90 transition-transform" />
            </button>
            <button onClick={() => setStep('idle')} className="col-span-2 p-2.5 bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-100 transition-colors flex items-center justify-center gap-2 text-[10px] font-black tracking-widest uppercase">
              <SafeIcon icon={FiX} /> {t('cancel_broadcast')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <button 
        onClick={step === 'idle' ? handleInitialClick : handleConfirmClick}
        disabled={isDisabled || step === 'select'}
        className={`group relative flex items-center justify-center w-28 h-28 sm:w-36 sm:h-36 rounded-full transition-all duration-500 transform active:scale-90 ${step === 'confirm' ? 'bg-red-600 shadow-[0_0_50px_rgba(220,38,38,0.5)] scale-105' : 'bg-slate-950 hover:bg-slate-900 shadow-2xl'} disabled:opacity-50 disabled:grayscale border-[6px] ${step === 'confirm' ? 'border-red-400' : 'border-slate-800/50'}`}
      >
        <div className={`absolute inset-0 rounded-full animate-ping ${step === 'confirm' ? 'bg-red-500/40' : 'bg-indigo-50/10'}`}></div>
        <div className="flex flex-col items-center">
          <SafeIcon icon={step === 'confirm' ? FiAlertCircle : FiShield} className={`text-4xl sm:text-5xl mb-1.5 transition-colors ${step === 'confirm' ? 'text-white' : 'text-indigo-400'}`} />
          <span className="text-sm sm:text-lg font-black tracking-tighter text-white uppercase">
            {step === 'confirm' ? t('send_label') : t('sos_label')}
          </span>
        </div>
      </button>
    </div>
  );
};

export default SOSButton;